/**
 * Asks this device, from where it is standing, whether the relay works.
 *
 * The question is not answerable anywhere else. A relay is reachable or not
 * *from a particular network*, and the ordinary failures — a UDP port closed in
 * a cloud firewall while the same port is open in `ufw`, a secret that differs
 * by a trailing space, a public address that is the machine's private one — all
 * leave a coturn that is running, listening and logging happily. The only
 * instrument that can see them is a browser on the network in question, which
 * on the case that matters is a phone on mobile data with the Wi-Fi turned off.
 *
 * `iceTransportPolicy: 'relay'` is what makes this a test rather than a call:
 * with it the connection is forbidden from gathering anything it can reach
 * directly, so a relay candidate appears if and only if the relay allocated one.
 * A real call has no such restriction, which is precisely why a broken relay
 * hides behind every call made on one Wi-Fi.
 *
 * On top of the combined answer, every TURN url is also asked *alone*, on its
 * own connection. One relay is three doors — UDP, TCP, TLS — and a browser
 * that got its allocation through the first says nothing about the other two,
 * while the network that blocks UDP is exactly the one where only the other
 * two matter. The per-url probes are what turns «релей отвечает» into «отвечает
 * по UDP, а оба TCP-пути этот оператор режет».
 *
 * Everything here is functions over the global `RTCPeerConnection`, read off
 * at call time, so all of it is testable without a network.
 */
import type { IceConfig } from '../api/client.ts';
import {
  describeTally,
  iceErrorName,
  iceErrorNote,
  tallyCandidates,
  type CandidateTally,
} from './callDiagnostics.ts';

/** One TURN url, asked on its own connection. */
export interface UrlProbe {
  url: string;
  /** Relay candidates this url produced by itself. */
  relay: number;
  /** From the probe's start to its first relay candidate. Null when none came. */
  firstRelayMs: number | null;
  /** Already worded, without the url — the line it goes into names that. */
  errors: string[];
  timedOut: boolean;
}

export interface RelayReport {
  tally: CandidateTally;
  /** One line per ICE server that refused, already in the app's wording. */
  errors: string[];
  /** Gathering did not finish in time. Usually a relay that answers nothing. */
  timedOut: boolean;
  /** False when the server has no relay configured at all — nothing to test. */
  configured: boolean;
  /** The doors one by one, in the order the server listed them. */
  probes: UrlProbe[];
}

/**
 * Long enough for a UDP allocation over a slow mobile link, short enough that
 * somebody holding a phone believes the answer is about their phone.
 *
 * Twelve rather than the original eight, because what has to fit in it grew.
 * It was chosen when /api/calls/ice offered three urls; it offers six now, and
 * a device with working IPv6 turns those into nine allocations — the primary
 * name is dual-stack, so each of its urls can be reached by either family,
 * while the IPv4-only turn4.<domain> yields one apiece. Every check run on the
 * day the sixth url appeared ended in `timedOut`, on two phones, with every
 * per-url probe passing: the pass was being cut off mid-gather and the count
 * reported was whatever had arrived by then. An iPhone said 4 one minute and 5
 * the next with nothing changed, which is the signature of a budget rather
 * than of a network.
 */
const GATHER_TIMEOUT_MS = 12_000;

interface Gathered {
  types: string[];
  /** The raw facts, so combined and per-url reports can word them differently. */
  errors: { errorCode: number; errorText: string; url: string }[];
  timedOut: boolean;
  firstRelayMs: number | null;
}

/** One relay-only gathering pass over the given servers. */
async function gather(iceServers: RTCIceServer[], timeoutMs: number): Promise<Gathered> {
  const startedAt = Date.now();
  const result: Gathered = { types: [], errors: [], timedOut: false, firstRelayMs: null };
  const connection = new RTCPeerConnection({ iceServers, iceTransportPolicy: 'relay' });

  try {
    // Something has to be negotiated or there is nothing to gather candidates
    // for. A data channel is the cheapest thing to want, and unlike a media
    // track it asks for no permission and lights no indicator.
    connection.createDataChannel('relay-check');

    const finished = new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => resolve(true), timeoutMs);
      connection.addEventListener('icegatheringstatechange', () => {
        if (connection.iceGatheringState !== 'complete') return;
        clearTimeout(timer);
        resolve(false);
      });
    });

    connection.addEventListener('icecandidate', (event) => {
      if (!event.candidate) return;
      const type = event.candidate.type ?? '';
      result.types.push(type);
      if (type === 'relay' && result.firstRelayMs === null) {
        result.firstRelayMs = Date.now() - startedAt;
      }
    });

    connection.addEventListener('icecandidateerror', (event) => {
      const error = event as RTCPeerConnectionIceErrorEvent;
      result.errors.push({
        errorCode: error.errorCode ?? 0,
        errorText: error.errorText ?? '',
        url: error.url ?? '',
      });
    });

    const offer = await connection.createOffer();
    await connection.setLocalDescription(offer);
    result.timedOut = await finished;
    return result;
  } finally {
    connection.close();
  }
}

/**
 * Every turn:/turns: url with the server entry that carries its credential.
 * stun: urls are left out: under a relay-only policy they can produce nothing,
 * so a probe of one could only ever say «молчит» about a server that is fine.
 */
function turnUrls(servers: RTCIceServer[]): { url: string; server: RTCIceServer }[] {
  const out: { url: string; server: RTCIceServer }[] = [];
  for (const server of servers) {
    const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
    for (const url of urls) {
      if (url.startsWith('turn:') || url.startsWith('turns:')) out.push({ url, server });
    }
  }
  return out;
}

/** The error as a probe line words it — its url is the line's subject already. */
function probeError(error: Gathered['errors'][number]): string | null {
  // 438 is the nonce refresh of a working relay; see iceErrorNote.
  if (error.errorCode === 438) return null;
  return [iceErrorName(error.errorCode), error.errorText || '—'].join(' · ');
}

export async function checkRelay(
  config: IceConfig,
  timeoutMs = GATHER_TIMEOUT_MS,
): Promise<RelayReport> {
  if (!config.hasRelay) {
    return { tally: tallyCandidates([]), errors: [], timedOut: false, configured: false, probes: [] };
  }

  // The combined pass and every probe run at once: the point of a probe is
  // which door it knocks on, not when, and in sequence four timeouts would
  // outlive the patience the check's one button implies.
  const [combined, ...probed] = await Promise.all([
    gather(config.iceServers, timeoutMs),
    ...turnUrls(config.iceServers).map(({ url, server }) =>
      gather([{ ...server, urls: url }], timeoutMs).then((result) => ({ url, result })),
    ),
  ]);

  const errors: string[] = [];
  for (const facts of combined.errors) {
    const note = iceErrorNote(facts);
    if (note) errors.push(note.message);
  }

  return {
    tally: tallyCandidates(combined.types),
    errors,
    timedOut: combined.timedOut,
    configured: true,
    probes: probed.map(({ url, result }) => ({
      url,
      relay: tallyCandidates(result.types).relay,
      firstRelayMs: result.firstRelayMs,
      errors: result.errors.map(probeError).filter((line): line is string => line !== null),
      timedOut: result.timedOut,
    })),
  };
}

/** The report as the one sentence somebody standing on a train needs. */
export function describeRelay(report: RelayReport): string {
  if (!report.configured) return 'Сервер работает без релея — TURN_HOST не задан';

  const trouble = report.errors.length > 0 ? ` · ${report.errors.join(' · ')}` : '';
  if (report.tally.relay > 0) {
    // «Сколько успело» when the budget ran out, because the count is then a
    // lower bound and reading it as a final one is how two phones get compared
    // on a number that is partly a race. The per-url lines below are unaffected
    // — each has the whole budget to itself.
    const cut = report.timedOut ? ' — успело за отведённое время, не итог' : '';
    return `Релей отвечает: ${report.tally.relay} кандидатов${cut}${trouble}`;
  }
  if (report.timedOut) {
    return `Релей молчит: за отведённое время не ответил ничем${trouble}`;
  }
  return `Релей не выделил ни одного адреса${trouble || ' · и не сказал почему'}`;
}

/**
 * `turns:turn4.makaek.com:443?transport=tcp` → `turn4 turns 443/tcp`.
 *
 * The host used to be left out, because there was one and repeating it six
 * times bought nothing. There are two now — the primary name and the IPv4-only
 * turn4.<domain> — and without the first label they render identically, so a
 * report reading «turn 3478/udp — работает» twice over one working door and
 * one broken one is indistinguishable from both working. Only the first piece
 * of the name: the rest is the same on every line and this is read on a phone.
 */
function shortUrl(url: string): string {
  const scheme = url.slice(0, url.indexOf(':'));
  const port = /:(\d+)(?:\?|$)/.exec(url)?.[1] ?? (url.startsWith('turns:') ? '5349' : '3478');
  const transport = /transport=(\w+)/.exec(url)?.[1] ?? 'udp';
  const host = url.slice(url.indexOf(':') + 1).split(/[:?]/)[0] ?? '';
  const label = host.split('.')[0];
  return `${label ? `${label} ` : ''}${scheme} ${port}/${transport}`;
}

/** One door's verdict, one line. */
export function describeProbe(probe: UrlProbe): string {
  const label = shortUrl(probe.url);
  if (probe.relay > 0) {
    const ms = probe.firstRelayMs !== null ? `, ответ за ${probe.firstRelayMs} мс` : '';
    return `${label} — работает${ms}`;
  }
  if (probe.errors.length > 0) return `${label} — ${probe.errors.join(' · ')}`;
  if (probe.timedOut) return `${label} — молчит`;
  return `${label} — ни адреса, ни ошибки`;
}

/**
 * The whole report in one string for `client_errors`, where an operator reads
 * it off the server after the person with the phone has gone home. Fits the
 * intake's message limit by construction: a handful of urls, a line each.
 */
export function relayCheckSummary(report: RelayReport): string {
  if (!report.configured) return 'no relay configured';
  const head = `${describeTally(report.tally)}${report.timedOut ? ' · timeout' : ''}`;
  return [head, ...report.probes.map(describeProbe)].join(' | ');
}
